import { supabase } from "@/integrations/supabase/client";
import logger from "@/utils/logger";

/**
 * UI Preferences stored in user's profile
 * Controls visibility of certain UI components and safe area overrides
 */
export interface UIPreferences {
  /** Show version link next to "Impostazioni" title */
  showVersionLink?: boolean;
  /** Show visual debugger card in settings */
  showVisualDebugger?: boolean;
  /** Safe area top override in pixels (null = auto-detect) */
  safeAreaTopOverride?: number | null;
  /** Safe area bottom override in pixels (null = auto-detect) */
  safeAreaBottomOverride?: number | null;
}

/**
 * Safe Area Configuration Limits - Top
 */
export const SAFE_AREA_LIMITS_TOP = {
  MIN: -40,
  MAX: 40,
  STEP: 4,
};

/**
 * Safe Area Configuration Limits - Bottom
 */
export const SAFE_AREA_LIMITS_BOTTOM = {
  MIN: 0,
  MAX: 60,
  STEP: 4,
};

/**
 * Deprecated: Use SAFE_AREA_LIMITS_TOP or SAFE_AREA_LIMITS_BOTTOM
 * Kept for backward compatibility
 */
export const SAFE_AREA_LIMITS = SAFE_AREA_LIMITS_TOP;

/**
 * Default UI preferences for new users
 * NOTE: These are the INITIAL defaults. Users can override them in the database.
 * When reading preferences with false values, they are from the database, not these defaults.
 */
const DEFAULT_UI_PREFERENCES: UIPreferences = {
  showVersionLink: false,
  showVisualDebugger: false,
  safeAreaTopOverride: null,
  safeAreaBottomOverride: null,
};

/**
 * Get UI preferences for the current user
 * @returns User's UI preferences or defaults if not set
 */
export async function getUiPreferences(): Promise<UIPreferences> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      logger.warn("[UI Prefs] User not authenticated");
      return DEFAULT_UI_PREFERENCES;
    }

    const { data, error } = await supabase
      .from("profiles")
      .select("ui_preferences")
      .eq("id", user.id)
      .single();

    if (error) {
      logger.warn("[UI Prefs] Error fetching from DB:", error);
      return DEFAULT_UI_PREFERENCES;
    }

    if (!data?.ui_preferences) {
      return DEFAULT_UI_PREFERENCES;
    }

    // Merge with defaults to ensure all properties exist
    return {
      ...DEFAULT_UI_PREFERENCES,
      ...data.ui_preferences,
    };
  } catch (error) {
    logger.error("[UI Prefs] Exception:", error);
    return DEFAULT_UI_PREFERENCES;
  }
}

/**
 * Update UI preferences for the current user
 * @param preferences Partial preferences to update
 * @returns Updated preferences
 */
export async function updateUiPreferences(
  preferences: Partial<UIPreferences>
): Promise<UIPreferences> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      throw new Error("User not authenticated");
    }

    // Get current preferences to merge
    const current = await getUiPreferences();
    const updated = {
      ...current,
      ...preferences,
    };

    const { data, error } = await supabase
      .from("profiles")
      .update({ ui_preferences: updated })
      .eq("id", user.id)
      .select("ui_preferences")
      .single();

    if (error) {
      throw error;
    }

    return data?.ui_preferences || updated;
  } catch (error) {
    logger.error("Error updating UI preferences:", error);
    throw error;
  }
}

/**
 * Set a specific UI preference
 * @param key The preference key
 * @param value The preference value
 */
export async function setUiPreference(
  key: keyof UIPreferences,
  value: boolean
): Promise<void> {
  const prefs = await getUiPreferences();
  await updateUiPreferences({
    ...prefs,
    [key]: value,
  });
}

/**
 * Get a specific UI preference value
 * @param key The preference key
 * @returns The preference value or default
 */
export async function getUiPreferenceValue(
  key: keyof UIPreferences
): Promise<boolean | number | null | undefined> {
  const prefs = await getUiPreferences();
  return prefs[key] ?? DEFAULT_UI_PREFERENCES[key];
}

/**
 * Update safe area top override
 * @param value Pixels or null for auto-detect
 */
export async function updateSafeAreaTop(value: number | null): Promise<void> {
  if (value !== null) {
    const clamped = Math.max(SAFE_AREA_LIMITS_TOP.MIN, Math.min(SAFE_AREA_LIMITS_TOP.MAX, value));
    const rounded = Math.round(clamped / SAFE_AREA_LIMITS_TOP.STEP) * SAFE_AREA_LIMITS_TOP.STEP;
    value = Math.max(SAFE_AREA_LIMITS_TOP.MIN, Math.min(SAFE_AREA_LIMITS_TOP.MAX, rounded));
  }
  await updateUiPreferences({ safeAreaTopOverride: value });
}

/**
 * Update safe area bottom override
 * @param value Pixels or null for auto-detect
 */
export async function updateSafeAreaBottom(value: number | null): Promise<void> {
  if (value !== null) {
    const clamped = Math.max(SAFE_AREA_LIMITS_BOTTOM.MIN, Math.min(SAFE_AREA_LIMITS_BOTTOM.MAX, value));
    const rounded = Math.round(clamped / SAFE_AREA_LIMITS_BOTTOM.STEP) * SAFE_AREA_LIMITS_BOTTOM.STEP;
    value = Math.max(SAFE_AREA_LIMITS_BOTTOM.MIN, Math.min(SAFE_AREA_LIMITS_BOTTOM.MAX, rounded));
  }
  await updateUiPreferences({ safeAreaBottomOverride: value });
}
